// The dashboard's per-framework readiness result (docs/design/slice4-certify-ux.md
// §4.1.2, §1.6). A COMPOUND component with `SelfAssessmentDisclosure` (§2.4): the two
// are the SAME render tree, never independently includable, so a future caller
// physically cannot render a framework's `qualifies`/`controls` without the
// disclosure rendering alongside it (the same guarantee CrewAnswer's mandatory
// Sources-on-answer gives Slice 3).
//
// KNOWN GAP, flagged (not invented around): the landed `Assessment` schema
// (packages/shared/src/model/certify.ts:149-158) carries NO `provisional` field - only
// `ProgrammaticReadiness` (the trust-page's public variant, :279-293) has the
// structural `provisional: boolean` ADR-0020 decision 2 amended in. This card therefore
// cannot honestly show "preliminary, pending the full standard" for a corpus_pending
// framework here - that honesty is fully satisfiable only on the Trust Page preview
// (TrustPageManagement.tsx), which reads the real `ProgrammaticReadiness.provisional`.
// Guessing "provisional" from `corpusVersion` (a free string, no pinned sentinel) would
// be inventing a signal the wire does not promise - flagged for the tech-lead instead
// (dispatch return), never silently fabricated.
import { certifyCopy } from "../copy/certify.js";
import type { Assessment } from "@nightcrew/shared";
import { needsFounderDecision } from "../api/certify.js";
import { SelfAssessmentDisclosure } from "./SelfAssessmentDisclosure.js";

const FRAMEWORK_LABELS: Record<Assessment["framework"], string> = {
  gdpr: "GDPR",
  soc2: "SOC 2",
  iso27001: "ISO 27001",
  nis2: "NIS2",
  hipaa: "HIPAA",
};

const QUALIFIES_KEY = {
  yes: "certify.dashboard.frameworkResult.qualifiesYes",
  partial: "certify.dashboard.frameworkResult.qualifiesPartial",
  no: "certify.dashboard.frameworkResult.qualifiesNo",
} as const;

const ControlRow = ({
  assessed,
}: {
  readonly assessed: Assessment["controls"][number];
}): React.JSX.Element => {
  const decisionPending = needsFounderDecision(assessed);
  let chipClass = "status-chip good";
  let tagText = certifyCopy("certify.dashboard.frameworkResult.controlRow.tagPass");
  if (decisionPending) {
    chipClass = "status-chip warn";
    tagText = certifyCopy("certify.dashboard.frameworkResult.controlRow.tagNeedsEvidence");
  } else if (assessed.result.status === "fail") {
    chipClass = "status-chip risk";
    tagText = certifyCopy("certify.dashboard.frameworkResult.controlRow.tagFail");
  } else if (assessed.result.status === "na") {
    chipClass = "status-chip neutral";
    tagText = "Not applicable";
  }

  return (
    <li className="control-row" data-testid="control-row" data-status={assessed.result.status}>
      <div className="grow">
        <p style={{ margin: 0, fontSize: 13.5 }}>{assessed.control.statement}</p>
        {assessed.result.evidence.length > 0 ? (
          <p className="muted" style={{ margin: 0, fontSize: 11.5 }}>
            {assessed.result.evidence.map((e) => e.ref).join(", ")}
          </p>
        ) : null}
      </div>
      <span className={chipClass}>{tagText}</span>
    </li>
  );
};

export const FrameworkResultCard = ({
  assessment,
  appName,
  onReassess,
}: {
  readonly assessment: Assessment;
  readonly appName: string;
  readonly onReassess?: () => void;
}): React.JSX.Element => {
  const frameworkName = FRAMEWORK_LABELS[assessment.framework];
  const passCount = assessment.controls.filter((c) => c.result.status === "pass").length;
  const totalCount = assessment.controls.length;
  const routedCount = assessment.controls.filter(needsFounderDecision).length;

  return (
    <div className="card pad framework-result" data-testid="framework-result" data-framework={assessment.framework}>
      <h3 className="h3">{certifyCopy("certify.dashboard.frameworkResult.heading", { frameworkName })}</h3>

      {/* STRUCTURAL: this disclosure is part of the SAME render as the framework
          result below it - there is no code path that renders the qualifies verdict
          or control list without it (§2.4). */}
      <div className="mt12">
        <SelfAssessmentDisclosure length="medium" appName={appName} frameworkName={frameworkName} />
      </div>

      <p className="mt12" style={{ fontWeight: 600 }}>
        {certifyCopy(QUALIFIES_KEY[assessment.qualifies], { passCount, totalCount })}
      </p>

      <ul className="control-list mt8" style={{ margin: 0, padding: 0, listStyle: "none" }}>
        {assessment.controls.map((assessed, index) => (
          <ControlRow key={`${assessed.control.controlKey}-${String(index)}`} assessed={assessed} />
        ))}
      </ul>

      <p className="muted mt12" style={{ fontSize: 11.5 }}>
        {certifyCopy("certify.dashboard.frameworkResult.lastAssessed", {
          assessedAtDate: new Date(assessment.assessedAt).toLocaleDateString(),
          corpusVersion: assessment.corpusVersion,
        })}
      </p>

      {routedCount > 0 ? (
        <p className="muted mt8" style={{ fontSize: 12 }}>
          {certifyCopy("certify.dashboard.frameworkResult.gapToShiftNote")}
        </p>
      ) : null}

      {onReassess ? (
        <button type="button" className="btn btn-ghost btn-sm mt12" onClick={onReassess}>
          {certifyCopy("certify.dashboard.frameworkResult.reassessCta")}
        </button>
      ) : null}
    </div>
  );
};
